import React, { useState, useMemo } from 'react';
import {Box, Button, Icon, Select, Tooltip} from '@neshane/blueberry';
import { useOperationsQueue, useOperationActions } from '../hooks';
import {
  OPERATION_TYPE_LABELS,
  PROVIDER_LABELS,
  OPERATION_STATUS_LABELS,
} from '../labels';
import { SearchFilter } from '../../../common/components';
import { OperationsPaginatedTable } from '../components/OperationsPaginatedTable';
import { OperationsPaginatedTableLoader } from '../components/OperationsTableLoader';

const STATUS_OPTIONS = Object.keys(OPERATION_STATUS_LABELS);

const STATUS_SELECT_OPTIONS = [
  { key: '', label: 'همه' },
  ...STATUS_OPTIONS.map(s => ({ key: s, label: OPERATION_STATUS_LABELS[s] })),
];

const TYPE_SELECT_OPTIONS = [
  { key: '', label: 'همه' },
  ...Object.entries(OPERATION_TYPE_LABELS).map(([key, label]) => ({ key, label })),
];

function OperationsQueueContainer() {
  const [status, setStatus] = useState('');
  const [operationType, setOperationType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { operations, total, loading, error, refresh } = useOperationsQueue({
    status: status || undefined,
    operationType: operationType || undefined,
  });
  const actions = useOperationActions();

  const selectedStatus =
    STATUS_SELECT_OPTIONS.find(o => o.key === status) || STATUS_SELECT_OPTIONS[0];
  const selectedType =
    TYPE_SELECT_OPTIONS.find(o => o.key === operationType) || TYPE_SELECT_OPTIONS[0];

  const filtered = useMemo(() => {
    if (!searchQuery) return operations;
    const query = searchQuery.toLowerCase();
    return operations.filter(
      op =>
        op.operationId?.toLowerCase().includes(query) ||
        (OPERATION_TYPE_LABELS[op.operationType] || op.operationType).toLowerCase().includes(query) ||
        (PROVIDER_LABELS[op.provider] || op.provider).toLowerCase().includes(query),
    );
  }, [operations, searchQuery]);

  const countBy = predicate => operations.filter(predicate).length;

  const statCards = [
    { icon: 'inbox-stack', label: 'در صف', value: countBy(o => o.status === 'QUEUED') },
    {
      icon: 'arrow-path',
      label: 'در حال اجرا',
      value: countBy(o => ['IN_PROGRESS', 'RETRYING'].includes(o.status)),
    },
    { icon: 'circle-check', label: 'موفق', value: countBy(o => o.status === 'SUCCEEDED') },
    {
      icon: 'triangle-warning',
      label: 'ناموفق / صف مرگ',
      value: countBy(o => ['FAILED', 'DEAD_LETTERED'].includes(o.status)),
    },
  ];

  if (loading) {
    return <OperationsPaginatedTableLoader />;
  }

  if (error) {
    return <p className="body-m text-secondary-alert-default">{error}</p>;
  }

  return (
    <div className="flex flex-col gap-24">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-4">
          <h1 className="heading-l text-primary">صف عملیات</h1>
          <p className="body-m text-secondary-bg-primary">
            همه عملیات خارجی ثبت‌شده در صف (تعداد کل: {total})
          </p>
        </div>
        <div className="flex items-center gap-8">
          <Button
            text="بروزرسانی"
            iconName="arrow-path"
            variant="secondaryNeutral"
            onClick={refresh}
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-16">
        {statCards.map(stat => (
          <Box key={stat.label} className="flex items-center gap-16 p-16">
            <span className="flex items-center justify-center rounded-12 bg-secondary-default p-12">
              <Icon name={stat.icon} size="l" />
            </span>
            <div className="flex flex-col gap-4">
              <h3 className="title-xl text-primary">{stat.value}</h3>
              <p className="body-s text-secondary-bg-primary">{stat.label}</p>
            </div>
          </Box>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-8">
        <SearchFilter
          search={searchQuery}
          setSearch={setSearchQuery}
          fit="content"
          placeholder="جستجو بر اساس شناسه، نوع عملیات یا سرویس..."
          minLength={1}
          className="w-[350px]"
        />
        <div className="flex flex-col gap-4">
          <label className="body-s text-secondary-bg-primary">وضعیت</label>
          <Tooltip content="لطفا این فیلد را پر کنید"><div style={{width:"100%"}}><Select
            fit="content"
            options={STATUS_SELECT_OPTIONS}
            value={selectedStatus}
            onSelect={option => setStatus(option.key)} required /></div></Tooltip>
        </div>
        <div className="flex flex-col gap-4">
          <label className="body-s text-secondary-bg-primary">نوع عملیات</label>
          <Tooltip content="لطفا این فیلد را پر کنید"><div style={{width:"100%"}}><Select
            fit="content"
            options={TYPE_SELECT_OPTIONS}
            value={selectedType}
            onSelect={option => setOperationType(option.key)} required /></div></Tooltip>
        </div>
      </div>

      <OperationsPaginatedTable
        operations={operations}
        filteredOperations={filtered}
        total={total}
        onRetry={actions.retry}
        onCancel={actions.cancel}
        busyId={actions.busyId}
      />
    </div>
  );
}

export default OperationsQueueContainer;
